import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import SectionItem from '../../../Layout/SectionItem/SectionItem'
import './ProjectVisibility.scss'
import { LuxRadioInput, LuxSelect, LuxDatePicker, Color } from '@lux/wardrobe-lib'
import { useIntl } from 'react-intl'
import { NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE } from '@/app/redux/reducers/activity-creation'
import { FormBase, SchedulingForm } from '@/app/models/new-activity-form'

const daysBeforeSelectOptions = [
    { value: 0, label: '0' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
]

const ProjectVisibility = (props: FormBase<SchedulingForm>) => {
    const { projectVisibilityOptions } = props
    const componentClass = 'smart-new-activity-scheduling-project-visibility'
    const intl = useIntl()
    const { form, onSetForm } = props
    const [specificDateHeight, setSpecificDateHeight] = useState('auto')
    const [isPickerVisible, setIsPickerVisible] = useState(false)

    useEffect(() => {
        function handleClickOutside(event) {
            const picker = document.getElementsByClassName('radio-specific-date-picker')[0]
            if (!!picker && !picker.contains(event.target)) {
                setIsPickerVisible(false)
            }
        }

        if (form.projectVisibility.value === projectVisibilityOptions[3].value) {
            const pickerIcon = document
                .getElementsByClassName(
                    'smart-new-activity-scheduling-project-visibility__radio-specific-datepicker'
                )[0]
                .getElementsByClassName('p-datepicker-trigger')[0]
            pickerIcon.addEventListener('click', () =>
                setIsPickerVisible(
                    !document.getElementsByClassName('radio-specific-date-picker')[0]
                )
            )

            // Bind the event listener
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [form.projectVisibility.value])

    const onFocus = () => {
        setIsPickerVisible(true)
    }

    const onVisibleChange = (e) => {}

    const onProjectVisibilityChange = (value) => {
        onSetForm({
            ...form,
            projectVisibility: { ...form.projectVisibility, value },
            daysBefore: {
                ...form.daysBefore,
                value:
                    form.projectVisibility.value !== projectVisibilityOptions[2].value
                        ? null
                        : form.daysBefore.value,
            },
            specificDate: {
                ...form.specificDate,
                value:
                    form.projectVisibility.value !== projectVisibilityOptions[3].value
                        ? null
                        : form.specificDate.value,
            },
        })
    }

    return (
        <SectionItem>
            <LuxRadioInput
                onChangeCallback={(value) => onProjectVisibilityChange(value)}
                value={form.projectVisibility.value}
                disabled={form.projectVisibility.disabled}
                title={intl.formatMessage({
                    id: 'modals.new.activity.section.scheduling.project.visibility',
                    defaultMessage: 'Project visibility',
                })}
                radioList={[projectVisibilityOptions[0]].map((opt) => ({
                    value: opt.value,
                    label: intl.formatMessage({
                        id: `modals.new.activity.section.scheduling.project.visibility.${opt.label}`,
                        defaultMessage: 'Immediate',
                    }),
                }))}
                name="projectVisibility"
                mandatory
            />
            <div className={`${componentClass}__radio-wrapper`}>
                <LuxRadioInput
                    onChangeCallback={(value) => onProjectVisibilityChange(value)}
                    value={form.projectVisibility.value}
                    disabled={form.projectVisibility.disabled}
                    radioList={[projectVisibilityOptions[1]].map((opt) => ({
                        value: opt.value,
                        label: intl.formatMessage({
                            id: `modals.new.activity.section.scheduling.project.visibility.${opt.label}`,
                            defaultMessage: 'Overnight',
                        }),
                    }))}
                    name="projectVisibility"
                />
            </div>
            <div className={`${componentClass}__radio-wrapper`}>
                <LuxRadioInput
                    onChangeCallback={(value) => onProjectVisibilityChange(value)}
                    value={form.projectVisibility.value}
                    disabled={form.projectVisibility.disabled}
                    radioList={[projectVisibilityOptions[2]].map((opt) => ({
                        value: opt.value,
                        label: intl.formatMessage({
                            id: `modals.new.activity.section.scheduling.project.visibility.${opt.label}`,
                            defaultMessage: 'Days before start date',
                        }),
                    }))}
                    name="projectVisibility"
                />
                {form.projectVisibility.value === projectVisibilityOptions[2].value && (
                    <div className={`${componentClass}__radio-days-before-content`}>
                        <div className={`${componentClass}__radio-days-before-content__select`}>
                            <LuxSelect
                                options={daysBeforeSelectOptions}
                                value={form.daysBefore.value}
                                disabled={form.daysBefore.disabled}
                                onChangeCallback={(value) =>
                                    onSetForm({
                                        ...form,
                                        daysBefore: { ...form.daysBefore, value },
                                    })
                                }
                                additionalProps={{
                                    appendTo: 'self',
                                }}
                            />
                        </div>
                        <span className="text">
                            {intl.formatMessage({
                                id: `modals.new.activity.section.scheduling.project.visibility.days.before.start.date.select`,
                                defaultMessage: 'number of days',
                            })}
                        </span>
                    </div>
                )}
            </div>
            <div
                className={`${componentClass}__radio-wrapper`}
                style={{ height: specificDateHeight }}>
                <LuxRadioInput
                    onChangeCallback={(value) => onProjectVisibilityChange(value)}
                    value={form.projectVisibility.value}
                    disabled={form.projectVisibility.disabled}
                    radioList={[projectVisibilityOptions[3]].map((opt) => ({
                        value: opt.value,
                        label: intl.formatMessage({
                            id: `modals.new.activity.section.scheduling.project.visibility.${opt.label}`,
                            defaultMessage: 'Specific date',
                        }),
                    }))}
                    name="projectVisibility"
                />
                {form.projectVisibility.value === projectVisibilityOptions[3].value && (
                    <div className={`${componentClass}__radio-specific-date-content`}>
                        <LuxDatePicker
                            intl={intl}
                            locale={props.language?.split('-')[0] || 'en'}
                            value={form.specificDate.value}
                            disabled={form.specificDate.disabled}
                            onChangeCallback={(value: Date) => {
                                onSetForm({
                                    ...form,
                                    specificDate: { ...form.specificDate, value },
                                })
                                setIsPickerVisible(false)
                            }}
                            minDate={new Date()}
                            style={{ backgroundColor: Color.WHITE }}
                            containerClasses={`${componentClass}__radio-specific-datepicker`}
                            additionalProps={{
                                panelClassName:
                                    'smart-wardrobe-datepicker radio-specific-date-picker',
                                appendTo: 'self',
                            }}
                            placeholder={intl.formatMessage({
                                id: 'modals.new.activity.section.scheduling.project.duration.specific.placeholder',
                                defaultMessage: 'Select date',
                            })}
                            visible={isPickerVisible}
                            onFocus={onFocus}
                            onVisibleChange={onVisibleChange}
                            showCustom
                        />
                    </div>
                )}
            </div>
        </SectionItem>
    )
}

const mapStateToProps = (state: any) => ({
    language: state.userReducer.user.language,
    projectVisibilityOptions:
        state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].projectVisibilityOptions,
})

const mapDispatchToProps = (dispatch: any) => ({})

export default connect(mapStateToProps, mapDispatchToProps)(ProjectVisibility)
